// PROPOSITO: la cola sin conexion. Lo que la alumna registra en el gimnasio se guarda aqui y sube
// solo cuando vuelve la senal. Alimenta la barra "Sin conexion · N por subir".
// DEPENDENCIAS: zustand/persist, src/types/dominio.
// NO OBVIO (1): cada accion lleva un clienteId generado en el telefono ANTES de subir. La base
// tiene un unique sobre esa columna, asi que si la cola reintenta no duplica la serie. Sin eso,
// el offline duplica datos en cuanto hay un reintento.
// NO OBVIO (2): este store no sube nada. Guarda y expone. Quien sube es
// src/services/sincronizacion.service.ts, que es la unica capa que habla con Supabase.

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { EstadoSincronizacion } from '@/types/dominio'

export type TipoDeAccion =
  | 'registrar_serie'
  | 'completar_sesion'
  | 'marcar_comida'
  | 'registrar_medicion'

export interface AccionPendiente {
  clienteId: string
  tipo: TipoDeAccion
  carga: Record<string, unknown>
  creadaAt: number
  intentos: number
}

interface EstadoCola extends EstadoSincronizacion {
  cola: AccionPendiente[]
  encolar: (accion: Omit<AccionPendiente, 'creadaAt' | 'intentos'>) => void
  confirmar: (clienteId: string) => void
  fallar: (clienteId: string) => void
  marcarEnLinea: (enLinea: boolean) => void
  marcarSincronizando: (sincronizando: boolean) => void
}

/** Identificador estable, generado en el telefono. Hace la subida idempotente. */
export function nuevoClienteId(): string {
  return crypto.randomUUID()
}

const MAX_INTENTOS = 8

export const usarCola = create<EstadoCola>()(
  persist(
    (set, get) => ({
      cola: [],
      enLinea: typeof navigator === 'undefined' ? true : navigator.onLine,
      pendientes: 0,
      ultimaSubida: null,
      sincronizando: false,

      encolar: accion => {
        const yaEsta = get().cola.some(a => a.clienteId === accion.clienteId)
        if (yaEsta) return
        const nueva: AccionPendiente = { ...accion, creadaAt: Date.now(), intentos: 0 }
        set(estado => ({ cola: [...estado.cola, nueva], pendientes: estado.cola.length + 1 }))
      },

      confirmar: clienteId => {
        set(estado => {
          const cola = estado.cola.filter(a => a.clienteId !== clienteId)
          return { cola, pendientes: cola.length, ultimaSubida: new Date().toISOString() }
        })
      },

      fallar: clienteId => {
        set(estado => {
          const cola = estado.cola
            .map(a => (a.clienteId === clienteId ? { ...a, intentos: a.intentos + 1 } : a))
            .filter(a => a.intentos < MAX_INTENTOS)
          return { cola, pendientes: cola.length }
        })
      },

      marcarEnLinea: enLinea => {
        set({ enLinea })
      },
      marcarSincronizando: sincronizando => {
        set({ sincronizando })
      },
    }),
    {
      name: 'florfit.cola',
      partialize: estado => ({ cola: estado.cola }),
      onRehydrateStorage: () => estado => {
        if (estado) estado.pendientes = estado.cola.length
      },
    },
  ),
)
